import {
  clearGlobalErrorMessage,
  isHandledByGlobalErrorHandler,
} from '../services/data-store';

/**
 * The message a page should present for a failed request, or null when the
 * backend classified the failure as internal and the generic system banner
 * already says everything worth saying.
 *
 * When the page does have something to say, the generic banner is taken down:
 * the page message is the more specific of the two, and showing both is the
 * bug this exists to prevent.
 *
 * Keeping the decision here means an `onError` handler stays a single
 * expression -- `setError(pageErrorMessage(err, 'Unable to save'))` -- instead
 * of every call site growing its own branch.
 */
export const pageErrorMessage = (error, message) => {
  if (isHandledByGlobalErrorHandler(error)) return null;
  clearGlobalErrorMessage();
  return message;
};

/**
 * Same decision, shaped as the `{ type, message }` alert/toast object the bid
 * pages hand to setAlert / setToaster. Those setters treat null as "clear".
 */
export const pageErrorAlert = (error, message, type = 'error') => {
  const shown = pageErrorMessage(error, message);
  return shown === null ? null : { type, message: shown };
};

/**
 * Same decision for pages that keep a *list* of notifications: `[notification]`
 * normally, `[]` when the generic banner already covers the failure.
 *
 * Returning a list rather than a nullable object keeps the call site a single
 * spread with no branch of its own:
 *
 *   setNotifications([
 *     ...pageErrorNotifications(err, { id, message, type }),
 *     ...rest,
 *   ]);
 */
export const pageErrorNotifications = (error, notification) => {
  if (isHandledByGlobalErrorHandler(error)) return [];
  clearGlobalErrorMessage();
  return [notification];
};
